// apps/web/src/primitives/NotificationBell.tsx
// Ops Notices — the global notification center, mounted once in App.tsx so it
// renders across every dashboard. Three layered cues by urgency:
//   · a fixed bottom-right BELL FAB with a severity-colored count badge,
//   · a dropdown PANEL (full list + snooze/dismiss + a "+ New notice" form),
//   · a fixed top CRITICAL BANNER box for critical notices (impossible to miss).
//
// Data comes from /api/notifications (rule-derived alerts + manual notices,
// merged server-side). Per feedback_no_fallbacks: on error/empty we render the
// bell with no badge — never fabricated notices. Styling uses theme CSS vars so
// it works under both the default and v2-dense themes.

import { useState, type CSSProperties } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { Notification, NotificationCategory, NotificationSeverity } from '@cf-apps/shared';

import {
  useNotifications,
  useCreateNotification,
  usePatchNotification,
} from '../data/queries/notifications.js';

// ─── Presentation helpers ────────────────────────────────────────────────────
function sevColor(s: NotificationSeverity): string {
  return s === 'critical' ? 'var(--color-fail)' : s === 'warning' ? 'var(--color-warn)' : 'var(--color-accent)';
}

const CATEGORY_LABEL: Record<NotificationCategory, string> = {
  reminder: 'reminder',
  action:   'action',
  error:    'error',
  cost:     'cost',
  info:     'info',
};

const SNOOZE_OPTIONS: ReadonlyArray<{ label: string; hours: number }> = [
  { label: '1d', hours: 24 },
  { label: '3d', hours: 72 },
  { label: '7d', hours: 168 },
];

// ─── Bell (exported; mounted in App.tsx) ─────────────────────────────────────
export function NotificationBell({ enabled }: { enabled: boolean }): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const { data } = useNotifications(enabled);
  const navigate = useNavigate();

  if (!enabled) return null;

  const items = data?.items ?? [];
  const counts = data?.counts ?? { critical: 0, warning: 0, info: 0, total: 0 };
  const critical = items.filter((it) => it.severity === 'critical');
  const badgeCount = counts.critical + counts.warning;
  const badgeColor = counts.critical > 0 ? 'var(--color-fail)' : counts.warning > 0 ? 'var(--color-warn)' : 'var(--color-text-4)';

  // Deep-link navigation for a notice action_url like '/operations?tab=params'.
  // Runtime URL → typed router is an API boundary; the cast is intentional.
  const go = (url: string): void => {
    const [path, qs] = url.split('?');
    const search = qs ? Object.fromEntries(new URLSearchParams(qs)) : undefined;
    void navigate({ to: path, search } as unknown as Parameters<typeof navigate>[0]);
    setOpen(false);
  };

  return (
    <>
      {critical.length > 0 && <CriticalBanner items={critical} onGo={go} onOpenPanel={() => setOpen(true)} />}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Notifications (${badgeCount} active)`}
        title="Notifications"
        data-testid="notification-bell"
        style={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          zIndex: 1800,
          width: 44,
          height: 44,
          borderRadius: '50%',
          border: '1px solid var(--color-border)',
          background: 'var(--color-bg-2)',
          color: 'var(--color-text-1)',
          fontSize: 20,
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <span aria-hidden>🔔</span>
        {badgeCount > 0 && (
          <span
            data-testid="notification-badge"
            style={{
              position: 'absolute',
              top: -4,
              right: -4,
              minWidth: 18,
              height: 18,
              padding: '0 5px',
              borderRadius: 9,
              background: badgeColor,
              color: '#0b0e13',
              fontSize: 11,
              fontWeight: 700,
              lineHeight: '18px',
              textAlign: 'center',
            }}
          >
            {badgeCount}
          </span>
        )}
      </button>

      {open && <NotificationPanel items={items} onClose={() => setOpen(false)} onGo={go} />}
    </>
  );
}

// ─── Critical banner (fixed top) ─────────────────────────────────────────────
function CriticalBanner({
  items,
  onGo,
  onOpenPanel,
}: {
  items: ReadonlyArray<Notification>;
  onGo: (url: string) => void;
  onOpenPanel: () => void;
}): JSX.Element {
  const patch = usePatchNotification();
  // Minimize-to-corner (operator ask 2026-07-22): the full-width bar overlaid the dashboard header,
  // so the only way to see the header was to SNOOZE the alert — silencing a live critical notice just
  // to read the page. Collapsing docks the banner to a small severity-colored pill (top-right) that
  // keeps the alert ACTIVE and one click re-expands it. Per-session React state only — no
  // browser-storage APIs in the dashboard; a reload legitimately re-asserts a still-active banner.
  const [collapsed, setCollapsed] = useState(false);

  if (collapsed) {
    return (
      <button
        type="button"
        data-testid="notification-critical-collapsed"
        onClick={() => setCollapsed(false)}
        title={`${items.length} critical notice${items.length === 1 ? '' : 's'} — click to expand`}
        aria-label={`${items.length} critical notices, minimized — expand`}
        style={{
          position: 'fixed',
          top: 8,
          right: 16,
          zIndex: 2000,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '4px 10px',
          borderRadius: 999,
          border: '1px solid var(--color-fail)',
          background: 'var(--color-bg-2)',
          color: 'var(--color-text-1)',
          fontSize: 12,
          fontWeight: 600,
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
        }}
      >
        <span aria-hidden style={{ color: 'var(--color-fail)' }}>⚠</span>
        {items.length} critical
      </button>
    );
  }

  return (
    <div
      data-testid="notification-critical-banner"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 2000,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      {items.map((it) => (
        <div
          key={it.id}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '8px 16px',
            background: 'var(--color-bg-2)',
            borderBottom: '2px solid var(--color-fail)',
            boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
          }}
        >
          <span aria-hidden style={{ color: 'var(--color-fail)', fontSize: 16 }}>⚠</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-1)' }}>{it.title}</div>
            {it.body && (
              <div style={{ fontSize: 12, color: 'var(--color-text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {it.body}
              </div>
            )}
          </div>
          {it.action_url && (
            <button type="button" onClick={() => onGo(it.action_url as string)} style={primaryBtn}>
              {it.action_label ?? 'Open'}
            </button>
          )}
          <button
            type="button"
            onClick={() => setCollapsed(true)}
            style={ghostBtn}
            data-testid="notification-critical-minimize"
            title="Minimize to corner (keeps the alert active — does NOT snooze)"
            aria-label="Minimize critical banner"
          >
            ─
          </button>
          <button
            type="button"
            onClick={() => patch.mutate({ id: it.id, action: 'snooze', snooze_hours: 24 })}
            style={ghostBtn}
            title="Snooze 1 day"
          >
            Snooze
          </button>
          {it.dismissable && (
            <button
              type="button"
              onClick={() => patch.mutate({ id: it.id, action: 'dismiss' })}
              style={ghostBtn}
              title="Dismiss"
            >
              ✕
            </button>
          )}
          <button type="button" onClick={onOpenPanel} style={ghostBtn} title="All notices">⋯</button>
        </div>
      ))}
    </div>
  );
}

// ─── Panel (dropdown) ────────────────────────────────────────────────────────
function NotificationPanel({
  items,
  onClose,
  onGo,
}: {
  items: ReadonlyArray<Notification>;
  onClose: () => void;
  onGo: (url: string) => void;
}): JSX.Element {
  const [showForm, setShowForm] = useState(false);
  return (
    <div
      data-testid="notification-panel"
      role="dialog"
      aria-label="Notifications"
      style={{
        position: 'fixed',
        bottom: 74,
        right: 20,
        zIndex: 1900,
        width: 380,
        maxWidth: 'calc(100vw - 40px)',
        maxHeight: '72vh',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--color-bg-2)',
        border: '1px solid var(--color-border)',
        borderRadius: 8,
        boxShadow: '0 6px 24px rgba(0,0,0,0.5)',
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid var(--color-border)' }}>
        <strong style={{ fontSize: 13, color: 'var(--color-text-1)' }}>Notices {items.length > 0 ? `(${items.length})` : ''}</strong>
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" onClick={() => setShowForm((v) => !v)} style={ghostBtn} data-testid="notification-new-toggle">
            {showForm ? 'Cancel' : '+ New'}
          </button>
          <button type="button" onClick={onClose} style={ghostBtn} aria-label="Close">✕</button>
        </div>
      </div>

      <div style={{ overflowY: 'auto', padding: 8 }}>
        {showForm && <NewNoticeForm onDone={() => setShowForm(false)} />}
        {items.length === 0 && !showForm && (
          <div style={{ padding: '24px 12px', textAlign: 'center', color: 'var(--color-text-4)', fontSize: 12 }}>
            No active notices. You’re all caught up.
          </div>
        )}
        {items.map((it) => (
          <NoticeRow key={it.id} item={it} onGo={onGo} />
        ))}
      </div>
    </div>
  );
}

// ─── One notice row ──────────────────────────────────────────────────────────
function NoticeRow({ item, onGo }: { item: Notification; onGo: (url: string) => void }): JSX.Element {
  const patch = usePatchNotification();
  return (
    <div
      data-testid={`notice-row-${item.id}`}
      style={{
        display: 'flex',
        gap: 10,
        padding: '10px 8px',
        borderBottom: '1px solid var(--color-border)',
      }}
    >
      <span aria-hidden style={{ width: 8, height: 8, borderRadius: '50%', background: sevColor(item.severity), marginTop: 5, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-1)' }}>{item.title}</span>
          <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--color-text-4)', border: '1px solid var(--color-border)', borderRadius: 3, padding: '0 4px' }}>
            {CATEGORY_LABEL[item.category]}
          </span>
        </div>
        {item.body && <div style={{ fontSize: 12, color: 'var(--color-text-3)', marginTop: 3, lineHeight: 1.4 }}>{item.body}</div>}
        <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {item.action_url && (
            <button type="button" onClick={() => onGo(item.action_url as string)} style={primaryBtn}>
              {item.action_label ?? 'Open'}
            </button>
          )}
          <span style={{ fontSize: 10, color: 'var(--color-text-4)' }}>snooze</span>
          {SNOOZE_OPTIONS.map((o) => (
            <button
              key={o.hours}
              type="button"
              onClick={() => patch.mutate({ id: item.id, action: 'snooze', snooze_hours: o.hours })}
              style={ghostBtn}
              title={`Snooze ${o.label}`}
            >
              {o.label}
            </button>
          ))}
          {item.dismissable && (
            <button type="button" onClick={() => patch.mutate({ id: item.id, action: 'dismiss' })} style={ghostBtn} title="Dismiss">
              Dismiss
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── "+ New notice" form ─────────────────────────────────────────────────────
function NewNoticeForm({ onDone }: { onDone: () => void }): JSX.Element {
  const create = useCreateNotification();
  const [title, setTitle] = useState('');
  const [severity, setSeverity] = useState<NotificationSeverity>('warning');
  const [category, setCategory] = useState<NotificationCategory>('reminder');
  const [body, setBody] = useState('');
  const [actionUrl, setActionUrl] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = (): void => {
    setError(null);
    const t = title.trim();
    if (t.length === 0) {
      setError('Title is required');
      return;
    }
    create.mutate(
      {
        title: t,
        severity,
        category,
        ...(body.trim() ? { body: body.trim() } : {}),
        ...(actionUrl.trim() ? { action_url: actionUrl.trim(), action_label: 'Open' } : {}),
      },
      {
        onSuccess: () => {
          setTitle('');
          setBody('');
          setActionUrl('');
          onDone();
        },
        onError: (e) => setError(e instanceof Error ? e.message : String(e)),
      },
    );
  };

  return (
    <div data-testid="notification-new-form" style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: 10, marginBottom: 8, background: 'var(--color-bg-3)', border: '1px solid var(--color-border)', borderRadius: 6 }}>
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Title (e.g. check X, remind me to Y)"
        maxLength={200}
        style={fieldStyle}
        aria-label="notice title"
        data-testid="notification-new-title"
      />
      <div style={{ display: 'flex', gap: 6 }}>
        <select value={severity} onChange={(e) => setSeverity(e.target.value as NotificationSeverity)} style={{ ...fieldStyle, flex: 1 }} aria-label="severity">
          <option value="info">info</option>
          <option value="warning">warning</option>
          <option value="critical">critical</option>
        </select>
        <select value={category} onChange={(e) => setCategory(e.target.value as NotificationCategory)} style={{ ...fieldStyle, flex: 1 }} aria-label="category">
          <option value="reminder">reminder</option>
          <option value="action">action</option>
          <option value="error">error</option>
          <option value="cost">cost</option>
          <option value="info">info</option>
        </select>
      </div>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Details (optional)"
        rows={2}
        maxLength={1000}
        style={{ ...fieldStyle, resize: 'vertical' }}
        aria-label="notice body"
      />
      <input
        type="text"
        value={actionUrl}
        onChange={(e) => setActionUrl(e.target.value)}
        placeholder="Deep link (optional), e.g. /operations?tab=params"
        maxLength={500}
        style={fieldStyle}
        aria-label="notice action url"
      />
      {error && <div style={{ fontSize: 11, color: 'var(--color-fail)' }}>{error}</div>}
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
        <button type="button" onClick={onDone} style={ghostBtn}>Cancel</button>
        <button type="button" onClick={submit} disabled={create.isPending} style={primaryBtn} data-testid="notification-new-submit">
          {create.isPending ? 'Adding…' : 'Add notice'}
        </button>
      </div>
    </div>
  );
}

// ─── Shared inline styles ────────────────────────────────────────────────────
const primaryBtn: CSSProperties = {
  fontSize: 11,
  padding: '3px 8px',
  borderRadius: 4,
  border: '1px solid var(--color-accent)',
  background: 'var(--color-accent)',
  color: '#0b0e13',
  cursor: 'pointer',
  fontWeight: 600,
};
const ghostBtn: CSSProperties = {
  fontSize: 11,
  padding: '3px 7px',
  borderRadius: 4,
  border: '1px solid var(--color-border)',
  background: 'transparent',
  color: 'var(--color-text-3)',
  cursor: 'pointer',
};
const fieldStyle: CSSProperties = {
  background: 'var(--color-bg-2)',
  color: 'var(--color-text-1)',
  border: '1px solid var(--color-border)',
  borderRadius: 4,
  padding: '4px 7px',
  fontSize: 12,
  fontFamily: 'inherit',
  width: '100%',
  boxSizing: 'border-box',
};
